/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import {
  DayOfWeek,
  ProviderData,
  WorkingSchedule,
} from '@dataTypes/provider.types'
import {
  createEmptyProviderData,
  getChangedProviderFields,
} from '@utils/provider.mapper'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { addProvider, updateProvider } from '@store/actions'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import Stepper from '@components/common/Stepper/Stepper.component'
import Button from '@components/common/Button/Button.component'
import WorkingDayBadge from '@components/common/WorkingDayBadge/WorkingDayBadge.component'
import ProviderPersonalInfo, {
  type ProviderPersonalInfoSyncRef,
} from './ProviderPersonalInfo'
import ProviderWorkShift from './ProviderWorkShift'
import ProviderReview from './ProviderReview'
import './AddProviderModal.css'

type AddProviderModalProps = {
  isOpen: boolean
  onRequestClose: () => void
  providerData?: ProviderData
  initialStep?: number
  activeProviderCount?: number
  providerSeatLimit?: number | null
  seatLimitReached?: boolean
}

const AddProviderModal: React.FC<AddProviderModalProps> = ({
  isOpen,
  onRequestClose,
  providerData,
  initialStep = 1,
  activeProviderCount,
  providerSeatLimit,
  seatLimitReached,
}) => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const clinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )

  const personalInfoRef = useRef<ProviderPersonalInfoSyncRef>(null)

  const [step, setStep] = useState<number>(initialStep)
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [formData, setFormData] = useState<ProviderData>({
    ...createEmptyProviderData(),
    clinicId,
  } as unknown as ProviderData)

  const [showDateOverrideForm, setShowDateOverrideForm] = useState(false)
  const [currentOverride, setCurrentOverride] = useState<WorkingSchedule>({
    day_of_week: DayOfWeek.SUNDAY,
    start_time: '',
    end_time: '',
  })
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setStep(initialStep)
    setValidationErrors({})
    setShowDateOverrideForm(false)
    setEditingIndex(null)
    setCurrentOverride({
      day_of_week: DayOfWeek.SUNDAY,
      start_time: '',
      end_time: '',
    })
    if (providerData) {
      setFormData(providerData)
    } else {
      setFormData({
        ...createEmptyProviderData(),
        clinicId,
        clinicIds: clinicId ? [clinicId] : [],
      } as unknown as ProviderData)
    }
  }, [isOpen, providerData, initialStep, clinicId])

  const steps = useMemo(() => ['Personal info', 'Work info', 'Review'], [])

  const originalWorkRole = useMemo(() => {
    if (!isOpen) return undefined
    return providerData?.workRole
  }, [isOpen, providerData?.workRole])

  const atProviderSeatLimit = useMemo(() => {
    if (seatLimitReached === true) return true
    if (
      providerSeatLimit != null &&
      activeProviderCount != null &&
      activeProviderCount >= providerSeatLimit
    ) {
      return true
    }
    return false
  }, [seatLimitReached, providerSeatLimit, activeProviderCount])

  const handleClose = useCallback((): void => {
    onRequestClose()
  }, [onRequestClose])

  const handleInputChange = useCallback(
    (field: string, value: string | boolean | number | object): void => {
      setFormData((prev) => ({ ...prev, [field]: value }) as ProviderData)
      setValidationErrors((prev) => {
        if (!prev[field]) return prev
        const next = { ...prev }
        delete next[field]
        return next
      })
    },
    [],
  )

  const handleNameBlur = useCallback(
    (field: string, value: string): void => {
      handleInputChange(field, value.trim())
    },
    [handleInputChange],
  )

  const toggleDateOverrideForm = useCallback((): void => {
    setShowDateOverrideForm((prev) => {
      if (prev) {
        setEditingIndex(null)
        setCurrentOverride({
          day_of_week: DayOfWeek.SUNDAY,
          start_time: '',
          end_time: '',
        })
      }
      return !prev
    })
  }, [])

  const handleShiftTimeChange = useCallback(
    (field: string, value: string): void => {
      handleInputChange(field, value)
    },
    [handleInputChange],
  )

  const handleClinicAssignmentChange = useCallback((comma: string): void => {
    const ids = comma
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    setFormData(
      (prev) =>
        ({
          ...prev,
          clinicIds: ids,
          clinicId: ids[0] ?? prev.clinicId ?? '',
        }) as ProviderData,
    )
  }, [])

  const renderWorkingDays = useCallback((): JSX.Element => {
    const displayLetters = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
    const schedules = formData.workingSchedules ?? []
    const start = formData.shiftStartTime?.trim() || '08:00:00'
    const end = formData.shiftEndTime?.trim() || '17:00:00'

    const toggleDay = (day: DayOfWeek): void => {
      const others = schedules.filter((s) => s.day_of_week !== day)
      const had = schedules.some((s) => s.day_of_week === day)
      if (had) {
        handleInputChange('workingSchedules', others)
      } else {
        const next: WorkingSchedule[] = [
          ...others,
          { day_of_week: day, start_time: start, end_time: end },
        ]
        handleInputChange('workingSchedules', next)
      }
    }

    return (
      <div className="modal-working-days-container">
        {Object.values(DayOfWeek).map((day, index) => (
          <WorkingDayBadge
            key={day}
            day={displayLetters[index]}
            isAvailable={schedules.some((s) => s.day_of_week === day)}
            title={day}
            onClick={() => toggleDay(day)}
          />
        ))}
      </div>
    )
  }, [
    formData.shiftEndTime,
    formData.shiftStartTime,
    formData.workingSchedules,
    handleInputChange,
  ])

  const removeDayOverride = useCallback((schedule: WorkingSchedule): void => {
    setFormData((prev) => ({
      ...prev,
      workingSchedules: prev.workingSchedules?.map((s) =>
        s.day_of_week === schedule.day_of_week
          ? {
              ...s,
              start_time: prev.shiftStartTime ?? '',
              end_time: prev.shiftEndTime ?? '',
            }
          : s,
      ),
    }))
  }, [])

  const saveNewOverride = useCallback((): void => {
    if (
      !currentOverride.day_of_week ||
      !currentOverride.start_time?.trim() ||
      !currentOverride.end_time?.trim()
    ) {
      return
    }

    setFormData((prev) => {
      const workingSchedules = [...(prev.workingSchedules || [])]
      const newOverrides = workingSchedules.map((schedule) => {
        if (schedule.day_of_week === currentOverride.day_of_week) {
          return {
            ...schedule,
            ...currentOverride,
          }
        }
        return schedule
      })

      return {
        ...prev,
        workingSchedules: newOverrides,
      }
    })
    setCurrentOverride({
      day_of_week: DayOfWeek.SUNDAY,
      start_time: '',
      end_time: '',
    })
    setEditingIndex(null)
    setShowDateOverrideForm(false)
  }, [currentOverride])

  const goToFirstStep = useCallback((): void => setStep(1), [])

  const onBack = useCallback((): void => {
    setStep((s) => Math.max(1, s - 1))
  }, [])

  const onNext = useCallback((): void => {
    // Minimal: allow navigation; detailed validation can be re-added later.
    setStep((s) => Math.min(steps.length, s + 1))
  }, [steps.length])

  const onSave = useCallback(async (): Promise<void> => {
    try {
      // Sync mobile/phone/work digits if the Personal step had masked inputs autofill.
      const synced = personalInfoRef.current?.syncAutofill?.() ?? {}
      const merged = { ...formData, ...synced } as unknown as ProviderData

      if (merged.providerId && providerData) {
        const changes = getChangedProviderFields(providerData, merged)
        await dispatch(
          updateProvider({ id: merged.providerId, provider: changes }),
        ).unwrap()
        showToast?.(createSuccessToast('Saved', 'Staff updated successfully'))
      } else {
        await dispatch(addProvider(merged)).unwrap()
        showToast?.(createSuccessToast('Saved', 'Staff added successfully'))
      }
      onRequestClose()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save staff'
      showToast?.(createFailedToast('Save failed', message))
    }
  }, [dispatch, formData, onRequestClose, providerData, showToast])

  const stepContent = useMemo((): React.ReactNode => {
    if (step === 1) {
      return (
        <ProviderPersonalInfo
          ref={personalInfoRef}
          providerData={formData}
          handleInputChange={handleInputChange}
          handleNameBlur={handleNameBlur}
          validationErrors={validationErrors}
          atProviderSeatLimit={atProviderSeatLimit}
          originalWorkRole={originalWorkRole}
        />
      )
    }
    if (step === 2) {
      return (
        <ProviderWorkShift
          providerData={formData}
          handleInputChange={handleInputChange}
          onClinicAssignmentChange={handleClinicAssignmentChange}
          removeDayOverride={removeDayOverride}
          saveNewOverride={saveNewOverride}
          renderWorkingDays={renderWorkingDays}
          showDateOverrideForm={showDateOverrideForm}
          currentOverride={currentOverride}
          setCurrentOverride={setCurrentOverride}
          editingIndex={editingIndex}
          setEditingIndex={setEditingIndex}
          toggleDateOverrideForm={toggleDateOverrideForm}
          validationErrors={validationErrors}
          handleShiftTimeChange={handleShiftTimeChange}
        />
      )
    }
    if (step === 3) {
      return <ProviderReview providerData={formData} onEdit={goToFirstStep} />
    }
    return null
  }, [
    step,
    formData,
    atProviderSeatLimit,
    originalWorkRole,
    handleClinicAssignmentChange,
    handleInputChange,
    handleNameBlur,
    validationErrors,
    removeDayOverride,
    saveNewOverride,
    renderWorkingDays,
    showDateOverrideForm,
    currentOverride,
    editingIndex,
    toggleDateOverrideForm,
    handleShiftTimeChange,
    goToFirstStep,
  ])

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      size="lg"
      className="custom-modal-width add-provider-modal"
      backdrop="static"
    >
      <Modal.Header className="bg-custom3 mb-3" closeButton>
        <Modal.Title>{providerData ? 'Edit staff' : 'Add staff'}</Modal.Title>
      </Modal.Header>

      <Stepper steps={steps} currentStep={step - 1} />

      <Modal.Body>
        <Form
          onSubmit={(e): void => {
            e.preventDefault()
          }}
        >
          {stepContent}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {step > 1 && (
          <Button className="button-secondary" onClick={onBack}>
            Back
          </Button>
        )}
        {step < steps.length ? (
          <Button className="button-primary" onClick={onNext}>
            Next
          </Button>
        ) : (
          <Button className="button-primary" onClick={onSave}>
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default AddProviderModal
